import React, { useState } from 'react';
import { Plus, Pencil, Trash2, Layers } from 'lucide-react';
import { useCrud } from '../../hooks/useApi';
import { processService } from '../../services/api';
import toast from 'react-hot-toast';
import FormModalLayout from '../../components/Common/FormModalLayout';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import Can from '../../components/Common/Can';
import { getAppHomePath } from '../../constants/navigation';
import { P } from '../../constants/permissions';
import { useTranslation } from 'react-i18next';
import { userFacingError } from '../../i18n/userFacingError';

const TiposMidia = () => {
  const { t } = useTranslation('process');

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formTipo, setFormTipo] = useState('');

  const { useList, useCreate, useUpdate, useDelete } = useCrud('tipos-midia', processService.tipoMidiaCrud);

  const { data: listData, isLoading, error } = useList();
  const items = Array.isArray(listData) ? listData : listData?.data ?? [];

  const createMutation = useCreate();
  const updateMutation = useUpdate();
  const deleteMutation = useDelete();

  const handleCreate = () => {
    setEditingItem(null);
    setFormTipo('');
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormTipo(item.tipo || '');
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Tem certeza que deseja excluir este tipo de mídia?')) {
      deleteMutation.mutate(id, {
        onSuccess: () => toast.success('Excluído com sucesso'),
        onError: (e) => toast.error(userFacingError(e, 'deleteError')),
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formTipo.trim()) {
      toast.error(t('mediaTypeRequired'));
      return;
    }
    if (editingItem) {
      updateMutation.mutate(
        { id: editingItem.id, data: { tipo: formTipo.trim() } },
        {
          onSuccess: () => {
            toast.success('Atualizado com sucesso');
            setShowModal(false);
            setEditingItem(null);
            setFormTipo('');
          },
          onError: (err) => toast.error(userFacingError(err, 'updateError')),
        }
      );
    } else {
      createMutation.mutate(
        { tipo: formTipo.trim() },
        {
          onSuccess: () => {
            toast.success('Criado com sucesso');
            setShowModal(false);
            setFormTipo('');
          },
          onError: (err) => toast.error(userFacingError(err, 'createError')),
        }
      );
    }
  };

  if (error) {
    return (
      <FormPageShell>
        <div className="alert alert-danger rounded-xl border border-red-200">
          <p className="text-red-800">{t('common:loadError')}</p>
        </div>
      </FormPageShell>
    );
  }

  return (
    <FormPageShell>
      <div className="space-y-6">
        <PageHeader
          title={t("mediaTitle")}
          description="Classificação usada em tramitações e anexos de processo."
          breadcrumbs={[
            { label: 'Início', to: getAppHomePath() },
            { label: 'Processos', to: '/process/cases' },
            { label: 'Tipos de mídia' },
          ]}
        >
          <Can permission={P.PROCESS_ACCESS}>
            <button type="button" onClick={handleCreate} className="btn btn-primary inline-flex items-center gap-2 shadow-sm">
              <Plus className="h-4 w-4" />
              Novo tipo
            </button>
          </Can>
        </PageHeader>

      <div className="data-list-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={2} className="px-6 py-8 text-center text-gray-500">
                    Carregando...
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.tipo}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <Can permission={P.PROCESS_ACCESS}>
                        <button
                          type="button"
                          onClick={() => handleEdit(item)}
                          className="text-primary-600 hover:text-primary-800 mr-4"
                        >
                          <Pencil className="w-4 h-4 inline" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(item.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-4 h-4 inline" />
                        </button>
                      </Can>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      </div>

      {showModal && (
        <FormModalLayout
          title={editingItem ? 'Editar tipo de mídia' : 'Novo tipo de mídia'}
          subtitle="Classificação usada no cadastro de documentos e anexos."
          headerIcon={Layers}
          historyEntityType="media_types"
          historyEntityId={editingItem?.id}
          onClose={() => {
            setShowModal(false);
            setEditingItem(null);
            setFormTipo('');
          }}
          size="md"
          footer={
            <>
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setEditingItem(null);
                  setFormTipo('');
                }}
                className="btn btn-outline"
              >
                Cancelar
              </button>
              <Can permission={P.PROCESS_ACCESS}>
                <button type="submit" form="tipo-midia-form" className="btn btn-primary">
                  {editingItem ? 'Salvar' : 'Criar'}
                </button>
              </Can>
            </>
          }
        >
          <form id="tipo-midia-form" onSubmit={handleSubmit} className="form-stack">
            <div className="form-group">
              <label htmlFor="tipo-midia-nome" className="form-label form-label-required">
                Tipo
              </label>
              <input
                id="tipo-midia-nome"
                type="text"
                value={formTipo}
                onChange={(e) => setFormTipo(e.target.value)}
                className="form-input"
                placeholder="Ex.: digital, físico"
              />
            </div>
          </form>
        </FormModalLayout>
      )}
    </FormPageShell>
  );
};

export default TiposMidia;
